import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import RegisterLinks from '../../components/RegisterLinks/RegisterLinks';
import Tabs from '../../components/Tab/TabControlled';
import Typography from '../../components/Typography/CustomTypography';
import WrapperContainer from '../../components/Container/WrapperContainer';
import { infoColor, whiteColor } from '../../assets/jss/global';
import { Role } from '../../common/Constants';
import ViewHeader from '../../components/Header/ViewHeader';

import styles from '../../assets/jss/pages/FAQPageStyle';
import FreeLogin from '../../components/FreeEntrance/FreeLogin';

const useStyles = makeStyles(styles);

export const Header = (props) => {
    const { role, eventInfo } = props;
    const intl = useIntl();
    const [title, setTitle] = useState('faq.visitorFAQ');

    useEffect(() => {
        if (!_.isEmpty(eventInfo)) {
            switch (role) {
                case Role.viewer:
                    setTitle(intl.formatMessage({ id: 'faq.viewer' }, {
                        text: eventInfo?.settings_texts_sidebar_viewer?.field_name?.faq || intl.formatMessage({ id: 'faq.pageTitle' }),
                    }));
                    break;
                case Role.organizer:
                    setTitle(intl.formatMessage({ id: 'faq.organizer' }, {
                        text: eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq || intl.formatMessage({ id: 'faq.pageTitle' }),
                    }));
                    break;
                default:
                    setTitle(intl.formatMessage({ id: 'faq.organizer' }, {
                        text: eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq || intl.formatMessage({ id: 'faq.pageTitle' }),
                    }));
                    break;
            }
        }
    }, [role, eventInfo]);

    return (
        <>
            {!_.isEmpty(eventInfo) && (
                <ViewHeader
                    eventName={eventInfo.name}
                    title={title}
                    eventInfo={eventInfo}
                />
            )}
        </>
    );
};

Header.defaultProps = {
    role: NaN,
    eventInfo: {},
};

Header.propTypes = {
    role: PropTypes.number,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

const FAQ = ({
    role, QAList, getQAList, clearQAList, eventInfo, isAuthorized, isCheckEvent, customColorQA, locale,
}) => {
    const classes = useStyles({ customColorQA, role });
    const intl = useIntl();
    const { shortName } = useParams();
    const [tab, setTab] = useState(0);

    useEffect(() => {
        getQAList(shortName, { type: Number(role === Role.organizer) }, locale);
        return clearQAList;
    }, [role, shortName, getQAList, clearQAList, locale]);

    useEffect(() => setTab(Number(role === Role.organizer)), [role]);

    const handleChangeTab = (type) => {
        getQAList(shortName, { type }, locale);
        setTab(type);
    };

    const renderTextTitle = () => {
        let titleText = '';
        switch (role) {
            case Role.viewer:
                titleText = eventInfo?.settings_texts_sidebar_viewer?.field_name?.faq
                    ? `入場者向け${eventInfo?.settings_texts_sidebar_viewer?.field_name?.faq}`
                    : intl.formatMessage({ id: 'faq.visitorFAQ' });
                break;

            case Role.organizer:
                titleText = eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq
                    ? `出展者向け${eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq}`
                    : intl.formatMessage({ id: 'faq.organizerFAQ' });
                break;

            default:
                titleText = eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq
                    ? `${eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq}`
                    : intl.formatMessage({ id: 'faq.pageTitle' });
                break;
        }
        return titleText;
    };

    const renderTitle = () => (
        <Box py={4}>
            <GridContainer>
                <GridItem align="center">
                    <Typography bold variant="h4" color={role === Role.viewer ? 'navy' : 'info'}>
                        {renderTextTitle()}
                    </Typography>
                </GridItem>
            </GridContainer>
        </Box>
    );

    const renderQA = () => (
        <>
            { Number.isNaN(role) ? (
                <Tabs
                    headers={[
                        intl.formatMessage({ id: 'faq.viewer' }, {
                            text: eventInfo?.settings_texts_sidebar_viewer?.field_name?.faq || intl.formatMessage({ id: 'faq.pageTitle' }),
                        }),
                        intl.formatMessage({ id: 'faq.organizer' }, {
                            text: eventInfo?.settings_texts_sidebar_organizer?.field_name?.faq || intl.formatMessage({ id: 'faq.pageTitle' }),
                        }),
                    ]}
                    onChangeTab={handleChangeTab}
                    tab={tab}
                    customColor={customColorQA?.contents_text_color}
                />
            ) : ''}
            {Array.isArray(QAList) && (QAList.length ? QAList.map((data) => (
                <React.Fragment key={data.id}>
                    <Box mt={4} py={2} px={4} className={classes.question}>
                        <Typography customColor={role === Role.organizer ? '' : customColorQA?.contents_text_color}>{`Q. ${data.question}`}</Typography>
                    </Box>
                    <Box display="flex">
                        <Box
                            width={63}
                            borderRight={
                                role === Role.organizer
                                    ? `2px dashed ${infoColor}`
                                    : ((customColorQA?.contents_text_color && `2px dashed ${customColorQA?.contents_text_color}`) || `2px dashed ${infoColor}`)
                            }
                            flexShrink={0}
                            className={classes.styleDiv}
                        />
                        <Box flexGrow={1}>
                            <Box height={24} className={classes.styleDiv} />
                            <Box display="flex">
                                <Box
                                    display="flex"
                                    width={63}
                                    flexDirection="column"
                                    flexShrink={0}
                                    className={classes.styleDiv}
                                >
                                    <Box flex={1} />
                                    <Box
                                        flex={1}
                                        borderTop={
                                            role === Role.organizer
                                                ? `2px dashed ${infoColor}`
                                                : ((customColorQA?.contents_text_color && `2px dashed ${customColorQA?.contents_text_color}`) || `2px dashed ${infoColor}`)
                                        }
                                        ml="-3px"
                                        bgcolor={(role !== Role.organizer && customColorQA?.screen_background_color) || whiteColor}
                                    />
                                </Box>
                                <Box py={2} px={4} className={classes.answer} flexGrow={1}>
                                    <Typography customColor={role === Role.organizer ? '' : customColorQA?.contents_text_color}>{`A. ${data.answer}`}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </React.Fragment>
            )) : (
                <GridItem
                    xs={12}
                    style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '20px',
                        zIndex: 1,
                        marginTop: '20px',
                        marginBottom: '40px',
                        color: role === Role.organizer ? '' : customColorQA?.contents_text_color,
                    }}
                >
                    <FormattedMessage id="noData" />
                </GridItem>
            ))}
        </>
    );

    const renderBottomButton = () => {
        if (!isAuthorized && isCheckEvent) {
            if (!(!eventInfo.free_seminar && !eventInfo.free_exhibition && !eventInfo.free_live_stream)) {
                return (
                    <GridItem
                        style={{
                            marginTop: '20px',
                        }}
                    >
                        <FreeLogin customColor={customColorQA} />
                    </GridItem>
                );
            }
            return (
                <GridItem
                    style={{
                        marginTop: '20px',
                    }}
                >
                    <RegisterLinks />
                </GridItem>
            );
        }
        return null;
    };

    return (
        <>
            {!_.isEmpty(eventInfo) && (
                <WrapperContainer className={classes.wrapper}>
                    <GridItem xs={12}>
                        {role === Role.organizer && renderTitle()}
                        {renderQA()}
                        {renderBottomButton()}
                    </GridItem>
                </WrapperContainer>
            )}
        </>
    );
};

FAQ.defaultProps = {
    QAList: [],
    eventInfo: {},
    isCheckEvent: false,
    customColorQA: {},
    locale: 'ja',
};

FAQ.propTypes = {
    getQAList: PropTypes.func.isRequired,
    clearQAList: PropTypes.func.isRequired,
    QAList: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.objectOf(PropTypes.string),
    ]),
    role: PropTypes.number.isRequired,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    isAuthorized: PropTypes.bool.isRequired,
    isCheckEvent: PropTypes.bool,
    customColorQA: PropTypes.oneOfType([() => null, PropTypes.object]),
    locale: PropTypes.string,
};

export default FAQ;
